import { ProceduralPoolGrid } from '../../../../Pool/logic/ProceduralPoolGrid';
import { LADDER_MODEL_CONFIG } from './config';
import { computeLadderDimensionLabelAnchorY } from './labelAnchor';
import { getLadderHeightConfig, getLadderScale } from './utils';

/**
 * Получить индекс угла для лестницы
 */
export const getLadderCornerIndex = (index, quantity) => {
    if (quantity === 1) return 0;
    if (quantity === 2) return index === 0 ? 0 : 2;
    if (quantity === 3) return index === 0 ? 0 : (index === 1 ? 1 : 3);
    return index;
};

/**
 * Рассчитать позиции лестниц
 * @param {Object} poolDimensions - { length, width, depth }
 * @param {number} quantity
 * @param {string} poolType
 * @param {Object} ladder - данные лестницы
 * @returns {Array} - массив инстансов
 */
export const calculateLadderPositions = (poolDimensions, quantity = 1, poolType, ladder) => {
    if (!poolDimensions || quantity <= 0) return [];

    const { length, width, depth } = poolDimensions;
    const gridSystem = new ProceduralPoolGrid(length, width, depth, poolType);
    const { innerLength, innerWidth } = gridSystem.dims;
    const instances = [];
    const modelPath = ladder?.modelPath || '/models/ladders/ladder10.glb';
    const modelScale = getLadderScale(modelPath);

    // Y-позиция лестницы относительно верхней кромки внутренней стены чаши:
    // фиксируем верх лестницы у борта без дополнительной глубинной просадки.
    const wallTopY = gridSystem.dims.wallCenterY + gridSystem.dims.wallHeight / 2;
    const ladderTopOffset = getLadderHeightConfig(modelPath);
    const modelScaleY = Array.isArray(modelScale) ? (modelScale[1] ?? 1.0) : 1.0;
    const LADDER_TOP_DROP = 0.425;
    const yPosition = wallTopY - (ladderTopOffset * modelScaleY) - LADDER_TOP_DROP;

    // Фиксированные отступы от стены и от угла (не зависят от размера бассейна)
    const DISTANCE_FROM_INNER_WALL = -0.25;
    const DISTANCE_FROM_CORNER = 0;

    // Лестницы всегда на КОРОТКОЙ стороне бассейна. WALL_LEFT/WALL_RIGHT
    // (x = ±halfWidth) — верная схема (подтверждено визуально) именно при
    // length > width. Если width > length — короткая пара становится
    // WALL_FRONT/WALL_BACK (z = ±halfLength), схема зеркальная по x/z.
    // Раньше здесь было ровно наоборот (условие перепутано местами) —
    // length > width ошибочно уводило лестницы на длинную сторону.
    const isLengthLonger = length > width;

    for (let i = 0; i < quantity; i++) {
        const cornerIndex = getLadderCornerIndex(i, quantity);
        let x = 0;
        let z = 0;
        let rotY = 0;

        const halfWidth = innerWidth / 2;
        const halfLength = innerLength / 2;

        if (isLengthLonger) {
            // Короткая сторона — WALL_LEFT/WALL_RIGHT (x фиксирован)
            switch (cornerIndex) {
                case 0:
                    x = halfWidth + DISTANCE_FROM_INNER_WALL;
                    z = halfLength - DISTANCE_FROM_CORNER;
                    rotY = Math.PI / 2;
                    break;
                case 1:
                    x = halfWidth + DISTANCE_FROM_INNER_WALL;
                    z = -halfLength + DISTANCE_FROM_CORNER;
                    rotY = -Math.PI / 2;
                    break;
                case 2:
                    x = -halfWidth - DISTANCE_FROM_INNER_WALL;
                    z = -halfLength + DISTANCE_FROM_CORNER;
                    rotY = -Math.PI / 2;
                    break;
                case 3:
                    x = -halfWidth - DISTANCE_FROM_INNER_WALL;
                    z = halfLength - DISTANCE_FROM_CORNER;
                    rotY = Math.PI / 2;
                    break;
                default:
                    x = halfWidth + DISTANCE_FROM_INNER_WALL;
                    z = halfLength - DISTANCE_FROM_CORNER;
                    rotY = -Math.PI / 2;
            }
        } else {
            // Короткая сторона — WALL_FRONT/WALL_BACK (z фиксирован),
            // зеркальный аналог схемы выше (x <-> z, поворот 0/180 вместо ±90).
            // Владелец подтвердил на скриншоте: модель стояла к бортику
            // задом наперёд — довернул на 180° (0 <-> π поменяны местами).
            switch (cornerIndex) {
                case 0:
                    z = halfLength + DISTANCE_FROM_INNER_WALL;
                    x = halfWidth - DISTANCE_FROM_CORNER;
                    rotY = Math.PI;
                    break;
                case 1:
                    z = halfLength + DISTANCE_FROM_INNER_WALL;
                    x = -halfWidth + DISTANCE_FROM_CORNER;
                    rotY = 0;
                    break;
                case 2:
                    z = -halfLength - DISTANCE_FROM_INNER_WALL;
                    x = -halfWidth + DISTANCE_FROM_CORNER;
                    rotY = 0;
                    break;
                case 3:
                    z = -halfLength - DISTANCE_FROM_INNER_WALL;
                    x = halfWidth - DISTANCE_FROM_CORNER;
                    rotY = Math.PI;
                    break;
                default:
                    z = halfLength + DISTANCE_FROM_INNER_WALL;
                    x = halfWidth - DISTANCE_FROM_CORNER;
                    rotY = Math.PI;
            }
        }

        const instance = {
            position: [x, yPosition, z],
            rotation: [0, rotY, 0],
            scale: modelScale,
            ladder: { ...ladder, modelPath },
            index: i,
            id: `ladder-${i}`,
            cornerIndex
        };
        instance.labelAnchorY = computeLadderDimensionLabelAnchorY(instance);
        instances.push(instance);
    }

    return instances;
};
